import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { Card } from "../../ui/Card";
import { PageHeader } from "./shared";

type Outlet = { outletId: string; outletName: string };
type SessUser = { firstName?: string; lastName?: string };
type Session = { ok: boolean; user?: SessUser; outlets?: Outlet[]; market?: string };

const TABS = [
  { id: "account", label: "Account" },
  { id: "outlets", label: "Outlets" },
  { id: "team",    label: "Team" },
] as const;

export function Settings() {
  const params = useParams<{ tab?: string }>();
  const active = (params.tab as (typeof TABS)[number]["id"]) ?? "account";
  const [sess, setSess] = useState<Session | null>(null);
  useEffect(() => {
    void fetch("/api/auth/session").then((r) => r.json()).then(setSess).catch(() => {});
  }, []);
  const user = sess?.user;

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Account, outlets, and team." />
      <nav className="flex gap-1 border-b border-line">
        {TABS.map((t) => (
          <a
            key={t.id}
            href={`/app/settings/${t.id}`}
            className={
              "px-4 py-2 text-sm transition border-b-2 -mb-px " +
              (active === t.id
                ? "border-nomni-green text-spinach font-medium"
                : "border-transparent text-ink-2 hover:text-ink")
            }
          >
            {t.label}
          </a>
        ))}
      </nav>

      {active === "account" && (
        <Card>
          <h3 className="font-medium text-ink mb-3">Your account</h3>
          <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-ink-mute">Name</dt>
            <dd className="text-ink">{user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "—" : "—"}</dd>
            <dt className="text-ink-mute">Market</dt>
            <dd className="text-ink-2">{sess?.market ?? "—"}</dd>
            <dt className="text-ink-mute">Outlets</dt>
            <dd className="text-ink-2">{sess?.outlets?.length ?? 0} connected</dd>
          </dl>
        </Card>
      )}

      {active === "outlets" && (
        <Card>
          <h3 className="font-medium text-ink mb-3">Your outlets</h3>
          {(sess?.outlets ?? []).length === 0 ? (
            <p className="text-ink-mute text-sm">No outlets on this account.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {(sess?.outlets ?? []).map((o) => (
                <li key={o.outletId} className="flex items-center justify-between border-b border-line/50 last:border-0 py-1">
                  <span className="text-ink">{o.outletName}</span>
                  <span className="text-ink-mute tabular-nums">{o.outletId}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {active === "team" && (
        <Card>
          <h3 className="font-medium text-ink mb-2">Team members</h3>
          <p className="text-ink-2 text-sm">
            Team management lives on the Zeemart settings surface and isn't ported in this rebuild yet. Aiko can answer team questions through the chat surface.
          </p>
        </Card>
      )}
    </div>
  );
}
